import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useAuth as useOIDCAuth } from 'react-oidc-context';
import axios from 'axios';
import { User, UserRegistration } from '../types/auth';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (
    credentials: { email: string; password: string },
    rememberMe?: boolean,
    loginType?: 'student' | 'company'
  ) => Promise<void>;
  register: (userData: UserRegistration) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, password: string) => Promise<void>;
  clearError: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Cookieから値を取得するヘルパー関数
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const oidcAuth = useOIDCAuth();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // 初期化時に認証状態をチェック
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // /api/auth/meエンドポイントで認証状態を確認
      const response = await axios.get('/api/auth/me');

      if (response.data.success) {
        setState((prev) => ({
          ...prev,
          user: response.data.data,
          isAuthenticated: true,
          isLoading: false,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        }));
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setState((prev) => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }));
    }
  };

  const login = async (
    credentials: { email: string; password: string },
    rememberMe = false,
    loginType?: 'student' | 'company'
  ) => {
    try {
      setState((prev) => ({ ...prev, error: null }));

      const response = await axios.post('/api/auth/login', {
        ...credentials,
        rememberMe,
        loginType,
      });

      if (response.data.success) {
        setState((prev) => ({
          ...prev,
          user: response.data.data.user,
          isAuthenticated: true,
          error: null,
        }));
      } else {
        throw new Error(
          response.data.error?.message || 'ログインに失敗しました'
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        'ログインに失敗しました';
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw error;
    }
  };

  const register = async (userData: UserRegistration) => {
    try {
      setState((prev) => ({ ...prev, error: null }));

      const response = await axios.post('/api/auth/register', userData);

      if (response.data.success) {
        // 登録成功後、自動ログインしない
        // ユーザーにはメール認証が必要なことを知らせる
        return response.data.data;
      } else {
        throw new Error(response.data.error?.message || '登録に失敗しました');
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        '登録に失敗しました';
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 [LOGOUT] Starting logout process...');
      
      // サーバー側のCookieをクリア
      await axios.post('/api/auth/logout');
      console.log('✅ [LOGOUT] Server-side cookies cleared');
      
      // Google認証（OIDC）のトークンをクリア
      if (oidcAuth.user) {
        await oidcAuth.removeUser();
        console.log('✅ [LOGOUT] OIDC tokens cleared from localStorage');
      }
    } catch (error) {
      console.error('❌ [LOGOUT] Logout error:', error);
    } finally {
      // 認証状態をリセット
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      console.log('✅ [LOGOUT] Auth state reset');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setState((prev) => ({ ...prev, error: null }));

      const response = await axios.post('/api/auth/reset-password', { email });

      if (!response.data.success) {
        throw new Error(
          response.data.error?.message || 'パスワードリセットに失敗しました'
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        'パスワードリセットに失敗しました';
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw error;
    }
  };

  const confirmPasswordReset = async (token: string, password: string) => {
    try {
      setState((prev) => ({ ...prev, error: null }));

      const response = await axios.post('/api/auth/confirm-reset', {
        token,
        newPassword: password,
      });

      if (!response.data.success) {
        throw new Error(
          response.data.error?.message || 'パスワードリセットに失敗しました'
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        'パスワードリセットに失敗しました';
      setState((prev) => ({ ...prev, error: errorMessage }));
      throw error;
    }
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post('/api/auth/refresh');

      if (!response.data.success) {
        throw new Error('トークンの更新に失敗しました');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // リフレッシュに失敗した場合はログアウト
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      throw error;
    }
  };

  const clearError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    resetPassword,
    confirmPasswordReset,
    clearError,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
